import { useRouter } from "next/router";
import Head from "next/head";

import { productTypesStaticData } from "data/categories";
import SeoBreadcrumb from "./breadcrumb";
import RichSnippets from "./richsnippets";
import { CANONICAL_HOST } from "lib/http";
import { SubcategoriesStaticData } from "data/categories";

function getProductRichSnippets(product, category) {
  const name = [product.product_type, product.subcategory1, product.brand, product.model_reference]
    .filter((x) => !!x)
    .join(" – ");
  const url = `${CANONICAL_HOST}/reconditionne/${category.slug}/${product.subcategory1_slug}/${product.slug}`;
  const description = `Ce ${name} fonctionne encore très bien ! Cet appareil a quelques rides de sa première vie. Tous nos appareils sont testés, réparés, nettoyés en profondeur et emballés par nos soins. C'est pourquoi nous vous offrons une garantie de deux ans !`;
  const price_withvat = (product.price / 100).toFixed(2).toString();
  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name,
    image: product.picture_new || product.available_appearances[0]?.picture,
    description,
    mpn: product.model_reference,
    brand: {
      "@type": "Brand",
      name: product.brand,
    },
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "EUR",
      price: price_withvat,
      availability: "https://schema.org/InStock",
    },
  };
}

function getTitle(category, subcategory1) {
  if (subcategory1) {
    return `${subcategory1.name} durable, reconditionné ou neuf durable | Murfy`;
  }
  if (category?.seo?.title) {
    return category.seo.title;
  }

  return `${category.name} durable, reconditionné ou neuf durable | Murfy`;
}

function getDescription(category, subcategory1) {
  if (subcategory1) {
    return `Achetez votre ${category.name} ${subcategory1.name} reconditionné chez Murfy, garantie 2 ans, livraison rapide : faites des économies et préservez la planète.`;
  }

  const staticCategory = productTypesStaticData.find((c) => c.slug === category.slug);
  if (staticCategory?.seo?.description) {
    return staticCategory.seo.description;
  }

  return `Achetez votre ${category.name} reconditionné chez Murfy, garantie 2 ans, livraison rapide : faites des économies et préservez la planète.`;
}

export default function SeoCatalog({ models }) {
  const { query } = useRouter();
  const staticCategory = productTypesStaticData.find((c) => c.slug === query.category) || {
    name: "Catalogue électroménager",
    slug: "catalogue",
  };
  const staticSubcategory = query.category1
    ? SubcategoriesStaticData.find((cat) => cat.slug === query.category1)
    : null;

  const canonical = staticSubcategory
    ? `${CANONICAL_HOST}/reconditionne/${staticCategory.slug}/${staticSubcategory.slug}`
    : `${CANONICAL_HOST}/reconditionne/${staticCategory.slug}`;

  const url = query.category1
    ? `${CANONICAL_HOST}/reconditionne/${query.category}/${query.category1}`
    : `${CANONICAL_HOST}/reconditionne/${query.category}`;

  const title = getTitle(staticCategory, staticSubcategory);
  const description = getDescription(staticCategory, staticSubcategory);
  const products = models.map((product) => getProductRichSnippets(product, staticCategory));

  const data = {
    "@context": "http://schema.org",
    "@type": "CollectionPage",
    name: title,
    url: url,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: products,
    },
  };

  return (
    <>
      <SeoBreadcrumb category={staticCategory} subcategory1={staticSubcategory} />
      <RichSnippets data={data} />
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" key="canonical" href={canonical} />
        <meta property="og:title" key="og:title" content={title} />
        <meta property="og:description" key="og:description" content={description} />
      </Head>
    </>
  );
}
